import InfiniteScroll from 'react-infinite-scroll-component';
import get from 'ts-get';

import { useState } from 'react';
import { useRutilusQuery } from '../../../../api/graphql/useRutilusQuery';
import { AppDownloadModal } from '../../../../components/AppDownloadModal/AppDownloadModal';
import { AppStoreButtons } from '../../../../components/Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { FishCatchesIcon } from '../../../../components/Icons/FishCatchesIcon';
import { SinglePostListItem } from '../../../../components/PostsViewer/components/SinglePostListItem/SinglePostListItem';
import { Text } from '../../../../components/Text/Text';
import { reportMykissClientError } from '../../../../helpers/errorHandling/client';
import { getNodesFromConnection } from '../../../../helpers/graphql';
import {
  GetProfile_user,
  GetUsersPosts,
  GetUsersPostsVariables,
} from '../../../../interfaces/graphql';
import { getUsersPosts } from '../../../../queries/rutilus/Users';
import { NotFound } from '../../../NotFound/NotFound';
import { AnglerPostsViewLoader } from './AnglerPostsViewLoader';

interface IProps {
  nickname: string;
  user: GetProfile_user;
  usersOwnPage: boolean;
  className?: string;
}

export const AnglerPostsView = ({ nickname, usersOwnPage, className }: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const variables: GetUsersPostsVariables = { nickname };
  const { data, error, fetchMore } = useRutilusQuery<GetUsersPosts, GetUsersPostsVariables>(
    getUsersPosts,
    {
      variables,
    },
  );

  if (error) {
    return <NotFound />;
  }

  if (!data) {
    return <AnglerPostsViewLoader className={className} />;
  }

  const angler = data.user;
  const { hasNextPage, endCursor } = angler.posts.pageInfo;
  const anglerPosts = getNodesFromConnection(angler.posts);
  const emptyPostText = `It looks like ${nickname} hasn't posted anything yet`;

  const fetchNextPage = async (): Promise<unknown> =>
    fetchMore({
      variables: {
        ...variables,
        after: endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }): GetUsersPosts => {
        if (!fetchMoreResult.user.posts) {
          return prev;
        }
        return {
          user: {
            ...fetchMoreResult.user,
            posts: {
              ...fetchMoreResult.user.posts,
              edges: [
                ...get(prev, p => p.user.posts.edges, []),
                ...get(fetchMoreResult, r => r.user.posts.edges, []),
              ],
            },
          },
        };
      },
    }).catch(reportMykissClientError);

  return (
    <div className={className} data-testid="AnglerPostsView">
      <InfiniteScroll
        next={fetchNextPage}
        hasMore={hasNextPage}
        loader={<></>}
        dataLength={anglerPosts.length}
        className="bg-sea-foam flex size-full flex-col gap-y-4 px-0 pb-4 md:px-0"
      >
        {anglerPosts.map(post => {
          return (
            <SinglePostListItem
              className="border-lake-fog bg-sea-foam rounded-lg border-2"
              key={post._id}
              post={post}
              onNotLoggedIn={() => {
                setIsAppDownloadModalOpen(true);
              }}
            />
          );
        })}
      </InfiniteScroll>
      {anglerPosts.length === 0 && (
        <>
          {usersOwnPage ? (
            <div className="text-midnight mb-10 flex flex-col items-center text-center">
              <Text className="w-2/3 italic">Add your first post in the fishbrain app today!</Text>
              <div className="text-twilight">
                <FishCatchesIcon size={100} />
              </div>
              <AppStoreButtons source="AnglerPostsView" />
            </div>
          ) : (
            <div className="mb-8 flex flex-col items-center justify-center text-center">
              <Text className="w-5/6 sm:w-1/2">{emptyPostText}</Text>
              <div className="text-twilight">
                <FishCatchesIcon size={100} />
              </div>
            </div>
          )}
        </>
      )}
      <AppDownloadModal
        appDownloadSource="AnglerPostsView"
        modalHeader={`See all catches and posts from ${nickname} in the Fishbrain app`}
        modalText="Download the app to see more info about your favorite anglers, ability to follow them and see what they catch next"
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
        showTextOnMobile
      />
    </div>
  );
};
